import { BitReader, BitWriter } from "../bitstream.ts";

export const Btn = {
  Jump: 1 << 0,
  Crouch: 1 << 1,
  Primary: 1 << 2,
  Secondary: 1 << 3,
  Ability1: 1 << 4,
  Ability2: 1 << 5,
  Ultimate: 1 << 6,
  Reload: 1 << 7,
  Melee: 1 << 8,
  Ability3: 1 << 9,
  /** Weapon select (1 / 2 keys) and cycle (mouse wheel), for heroes who carry more than one. */
  Weapon1: 1 << 10,
  Weapon2: 1 << 11,
  WeaponNext: 1 << 12,
} as const;
const BUTTON_BITS = 13;

export interface InputCmd {
  seq: number; // u16, wraps
  buttons: number; // Btn bitmask
  moveX: number; // -1..1 (right +)
  moveZ: number; // -1..1 (forward +)
  yaw: number;
  pitch: number;
  /** Server tick (with fraction) the client was rendering remote entities at — for lag compensation. */
  renderTick: number;
}

export const emptyInput = (seq = 0): InputCmd => ({ seq, buttons: 0, moveX: 0, moveZ: 0, yaw: 0, pitch: 0, renderTick: 0 });

/** Quantizes move axes the same way the wire does, so predicted and server inputs are identical. */
export function quantizeInput(i: InputCmd): InputCmd {
  return {
    ...i,
    seq: i.seq & 0xffff,
    moveX: Math.round(Math.max(-1, Math.min(1, i.moveX)) * 127) / 127,
    moveZ: Math.round(Math.max(-1, Math.min(1, i.moveZ)) * 127) / 127,
    yaw: Math.fround(i.yaw),
    pitch: Math.fround(i.pitch),
  };
}

export function writeInput(w: BitWriter, i: InputCmd) {
  w.u16(i.seq);
  w.bits(i.buttons, BUTTON_BITS);
  w.int(Math.round(i.moveX * 127), 8);
  w.int(Math.round(i.moveZ * 127), 8);
  w.f32(i.yaw);
  w.f32(i.pitch);
  w.u32(Math.floor(i.renderTick));
  w.u8(Math.floor((i.renderTick % 1) * 255));
}

export function readInput(r: BitReader): InputCmd {
  const seq = r.u16();
  const buttons = r.bits(BUTTON_BITS);
  const moveX = r.int(8) / 127;
  const moveZ = r.int(8) / 127;
  const yaw = r.f32();
  const pitch = r.f32();
  const renderTick = r.u32() + r.u8() / 255;
  return { seq, buttons, moveX, moveZ, yaw, pitch, renderTick };
}

/** Sequence comparison with u16 wraparound: a > b. */
export const seqGreater = (a: number, b: number) => ((a - b) & 0xffff) !== 0 && ((a - b) & 0xffff) < 0x8000;
export const seqDiff = (a: number, b: number) => {
  const d = (a - b) & 0xffff;
  return d >= 0x8000 ? d - 0x10000 : d;
};
